import styled from "styled-components";

import Dropdown from "app/components/shared/Dropdown";
import Icon from "app/components/shared/Icon";
import { getEntries } from "app/components/analytics/shared/typeHelpers";
import {
  getIssueDescription,
  IssuesListProps,
} from "app/components/analytics/shared/Issues/getIssueContent";
import IssueBadge from "./IssueBadge";
import { track } from "app/lib/segmentAnalytics";
import { remove, isNumber } from "lodash";
import { useState, useEffect } from "react";

type IssuesDropdownProps = {
  totalIssues: number;
  issuesList: IssuesListProps;
  threshold: number;
  editThresholdUrl: string;
};

const DropdownButton = styled.button`
  border-radius: 30px;
  padding: 8px 17px;
`;

const IssueRow = styled.div`
  border-radius: 4px;
  border: 1px solid var(--gray-500);
  padding: 20px;

  &:not(:last-of-type) {
    margin-bottom: 15px;
  }
`;

const _trackDropdownClicked = ({
  issues,
  dropdownIsOpen,
}: {
  issues: Array<string | number>;
  dropdownIsOpen: boolean;
}) => {
  if (!dropdownIsOpen) {
    return;
  } // Only track the opening on the dropdown

  remove(issues, isNumber);

  track("Execution Issues Dropdown Opened", {
    source: "Execution Issues Dropdown",
    action: "view-issues-dropdown",
    issues: issues.toString(),
  });
};

const IssuesDropdown = ({
  issuesList,
  totalIssues,
  editThresholdUrl,
  threshold,
}: IssuesDropdownProps) => {
  const label = `${totalIssues} ${totalIssues > 1 ? "issues" : "issue"} found`;
  const issues = getEntries(issuesList).filter(([, value]) => value > 0);

  return (
    <Dropdown
      width={420}
      nibOffsetX={65}
      onToggle={(dropdownIsOpen) =>
        _trackDropdownClicked({
          issues: issues.flat(),
          dropdownIsOpen,
        })
      }
    >
      <DropdownButton className="btn border-red-700 red-700 flex items-center bg-white">
        <Icon
          icon="heroicons/outline/fire"
          style={{
            marginRight: "8px",
            width: "20px",
            stroke: "var(--red-500)",
          }}
        />
        {label}
        <Icon
          icon="heroicons/outline/chevron-down"
          style={{ marginLeft: "8px", width: "6px", stroke: "var(--red-500)" }}
        />
      </DropdownButton>

      <div className="p4">
        <div className="flex items-center bold mb5">
          <Icon
            icon="heroicons/outline/fire"
            style={{
              marginRight: "5px",
              width: "24px",
              stroke: "var(--red-500)",
            }}
          />
          {label}
        </div>
        {issues.map(([issue]) => (
          <IssueRow key={issue}>
            <IssueBadge issue={issue} urgent={issue === "flaky"} />
            <p className="mb0">
              {getIssueDescription({ issue, editThresholdUrl, threshold })}
            </p>
          </IssueRow>
        ))}
      </div>
    </Dropdown>
  );
};

export default IssuesDropdown;
